// Auto-Layout des Signalgraphen (dependency-frei).
//
// Der geseedete/gestempelte Graph trägt oft keine Positionen (position: null) → React-Flow
// verteilt die Knoten sonst als lesbares-Gitter-freies Durcheinander bzw. der Aufrufer legt ein
// Gitter darüber. Diese Funktion berechnet stattdessen ein topologisches Links-nach-rechts-Layout:
// Eingänge (Quellen) links, Rechenblöcke geschichtet dahinter, Ausgänge/Senken rechts. Jede Kante
// zeigt so überwiegend nach rechts.
//
// Schichtung: longest-path-Layering über die Kanten. Rückkopplungen (die ein reiner
// Signalgraph über Zustandsblöcke wie UNITDELAY/PT1/PID schließt) werden erkannt und als
// Rück-Kanten ignoriert, damit die Schichtung terminiert und die Zustandsblöcke die Zyklen
// „brechen" (sie sitzen an der Stelle ihrer Vorwärts-Kanten). Innerhalb einer Schicht werden die
// Knoten vertikal gleichmäßig verteilt.

import type { GraphEdge, GraphNode } from '../api/types';

export interface LayoutOptions {
  xGap?: number; // horizontaler Schichtabstand
  yGap?: number; // vertikaler Abstand innerhalb einer Schicht
  x0?: number; // linker Rand
  y0?: number; // oberer Rand
}

// Abstände Schicht-zu-Schicht (x) und Knoten-zu-Knoten innerhalb einer Schicht (y). Bewusst
// großzügig: die Signalknoten haben min. 144 px Breite und wachsen mit langen Labels (BORIS-Namen
// wie „Input Concentration") sowie einer Tag-Zeile in der Höhe. Zu enge Abstände ließen breite
// Boxen benachbarter Schichten optisch überlappen — daher deutlich mehr Luft als die reine Boxgröße.
export const LAYOUT_X_GAP = 300;
export const LAYOUT_Y_GAP = 150;

const DEFAULTS: Required<LayoutOptions> = {
  xGap: LAYOUT_X_GAP,
  yGap: LAYOUT_Y_GAP,
  x0: 80,
  y0: 60,
};

// Erkennt die Rück-Kanten eines gerichteten Graphen per DFS (grau = im aktuellen Pfad). Eine Kante
// auf einen grauen Knoten schließt einen Zyklus und wird als Rück-Kante markiert. Deterministisch
// über die Eingabereihenfolge der Knoten/Kanten.
function backEdgeSet(nodeIds: string[], adj: Map<string, { edgeIdx: number; to: string }[]>): Set<number> {
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>(nodeIds.map((id) => [id, WHITE]));
  const back = new Set<number>();

  // Iterativer DFS, um bei großen Graphen den Stack nicht zu sprengen.
  for (const start of nodeIds) {
    if (color.get(start) !== WHITE) continue;
    const stack: { node: string; i: number }[] = [{ node: start, i: 0 }];
    color.set(start, GRAY);
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      const edges = adj.get(frame.node) ?? [];
      if (frame.i < edges.length) {
        const { edgeIdx, to } = edges[frame.i];
        frame.i += 1;
        const c = color.get(to) ?? WHITE;
        if (c === GRAY) {
          back.add(edgeIdx); // Kante schließt den aktuellen Pfad → Rück-Kante
        } else if (c === WHITE) {
          color.set(to, GRAY);
          stack.push({ node: to, i: 0 });
        }
      } else {
        color.set(frame.node, BLACK);
        stack.pop();
      }
    }
  }
  return back;
}

// Longest-path-Schicht je Knoten (nur über Vorwärts-Kanten). Quellen liegen auf Schicht 0.
function layerOf(
  nodeIds: string[],
  forward: { from: string; to: string }[],
): Map<string, number> {
  const layer = new Map<string, number>(nodeIds.map((id) => [id, 0]));
  const outgoing = new Map<string, string[]>();
  const indeg = new Map<string, number>(nodeIds.map((id) => [id, 0]));
  for (const e of forward) {
    (outgoing.get(e.from) ?? outgoing.set(e.from, []).get(e.from)!).push(e.to);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }
  // Kahn-Reihenfolge über die Vorwärts-Kanten (azyklisch, da Rück-Kanten entfernt sind).
  const queue = nodeIds.filter((id) => (indeg.get(id) ?? 0) === 0);
  const deg = new Map(indeg);
  const orderVisited: string[] = [];
  while (queue.length > 0) {
    const n = queue.shift()!;
    orderVisited.push(n);
    for (const m of outgoing.get(n) ?? []) {
      layer.set(m, Math.max(layer.get(m) ?? 0, (layer.get(n) ?? 0) + 1));
      deg.set(m, (deg.get(m) ?? 0) - 1);
      if ((deg.get(m) ?? 0) === 0) queue.push(m);
    }
  }
  return layer;
}

// Berechnet Positionen {x,y} je Knoten-ID. Reine Funktion (keine React-Flow-Abhängigkeit),
// damit unmittelbar testbar. Die Reihenfolge innerhalb einer Schicht folgt der Eingabereihenfolge
// der Knoten (stabil).
// Klassisches Schichtenlayout (Sugiyama-artig, longest-path): X = Schicht, Y = Reihe.
function layeredLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  x0: number,
  y0: number,
  xGap: number,
  yGap: number,
): Map<string, { x: number; y: number }> {
  const nodeIds = nodes.map((n) => n.id);
  const idSet = new Set(nodeIds);
  const adj = new Map<string, { edgeIdx: number; to: string }[]>();
  const rawEdges = edges
    .map((e) => ({ from: e.from.node, to: e.to.node }))
    .filter((e) => idSet.has(e.from) && idSet.has(e.to) && e.from !== e.to);
  rawEdges.forEach((e, i) => {
    (adj.get(e.from) ?? adj.set(e.from, []).get(e.from)!).push({ edgeIdx: i, to: e.to });
  });
  const back = backEdgeSet(nodeIds, adj);
  const forward = rawEdges.filter((_, i) => !back.has(i));
  const layer = layerOf(nodeIds, forward);
  const byLayer = new Map<number, string[]>();
  for (const id of nodeIds) {
    const l = layer.get(id) ?? 0;
    (byLayer.get(l) ?? byLayer.set(l, []).get(l)!).push(id);
  }
  const pos = new Map<string, { x: number; y: number }>();
  for (const [l, ids] of byLayer) {
    ids.forEach((id, row) => {
      pos.set(id, { x: x0 + l * xGap, y: y0 + row * yGap });
    });
  }
  return pos;
}

// Gruppen-bewusstes Layout: gestempelte Graphen tragen je Muster (Konditionierungskette,
// Regelschleife, Soft-Sensor) ein `group.id`. Statt alle Knoten einer Schicht zu einer sehr hohen
// Spalte zu stapeln (bei 600+ Knoten → unlesbarer Streifen), wird JEDE Gruppe kompakt als
// Links-nach-rechts-Kette gelegt und die Gruppen als Kacheln in ein Raster verteilt — so bleibt der
// Graph annähernd quadratisch und die Verdrahtung je Kette erkennbar. Ohne Gruppen (Superblock-
// Drilldown, Tests) fällt es auf das klassische Schichtenlayout zurück.
export function computeLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  options: LayoutOptions = {},
): Map<string, { x: number; y: number }> {
  const opt = { ...DEFAULTS, ...options };
  const groupOf = (n: GraphNode): string | undefined => n.group?.id;
  const groupIds = [...new Set(nodes.map(groupOf).filter((g): g is string => !!g))];

  if (groupIds.length === 0) {
    return layeredLayout(nodes, edges, opt.x0, opt.y0, opt.xGap, opt.yGap);
  }

  const gxGap = 170;
  const gyGap = 72;
  const locals = new Map<
    string,
    { local: Map<string, { x: number; y: number }>; w: number; h: number }
  >();
  let maxW = 0;
  let maxH = 0;
  for (const gid of groupIds) {
    const gNodes = nodes.filter((n) => groupOf(n) === gid);
    const gset = new Set(gNodes.map((n) => n.id));
    const gEdges = edges.filter((e) => gset.has(e.from.node) && gset.has(e.to.node));
    const local = layeredLayout(gNodes, gEdges, 0, 0, gxGap, gyGap);
    let w = 0;
    let h = 0;
    for (const p of local.values()) {
      w = Math.max(w, p.x);
      h = Math.max(h, p.y);
    }
    locals.set(gid, { local, w, h });
    maxW = Math.max(maxW, w);
    maxH = Math.max(maxH, h);
  }

  const cols = Math.max(1, Math.ceil(Math.sqrt(groupIds.length)));
  const cellW = maxW + opt.xGap * 1.5;
  const cellH = maxH + opt.yGap;
  const pos = new Map<string, { x: number; y: number }>();
  groupIds.forEach((gid, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const ox = opt.x0 + col * cellW;
    const oy = opt.y0 + row * cellH;
    for (const [id, p] of locals.get(gid)!.local) {
      pos.set(id, { x: ox + p.x, y: oy + p.y });
    }
  });
  const ungrouped = nodes.filter((n) => !groupOf(n));
  if (ungrouped.length) {
    const baseY = opt.y0 + Math.ceil(groupIds.length / cols) * cellH;
    ungrouped.forEach((n, i) => pos.set(n.id, { x: opt.x0, y: baseY + i * opt.yGap }));
  }
  return pos;
}
